// GET /users/me · PATCH /users/me · PATCH /users/me/birth · PATCH /users/me/settings ·
// PATCH /users/me/vacation · POST /users/me/onboarding · DELETE /users/me
import { z } from 'zod'
import { genderSchema } from '../enums.js'
import { cityDtoSchema } from './city.js'
import {
  idSchema,
  isoDateSchema,
  isoDateTimeSchema,
  okResponseSchema,
  timeOfDaySchema,
} from './common.js'

// Профиль пользователя — как отдаёт бэкенд (snake_case).
export const userDtoSchema = z.object({
  id: idSchema,
  tg_id: z.string(),
  first_name: z.string(),
  gender: genderSchema.nullable(),
  photo_url: z.string().nullable(),
  username: z.string().nullable(),
  birth_date: isoDateSchema,
  birth_time: timeOfDaySchema.nullable(),
  birth_city_id: idSchema.nullable(),
  // Разворачиваем город целиком: клиенту нужно его название на экранах профиля и натальной карты,
  // а держать локальный кэш id→имя (он живёт только в том браузере, где город выбирали) — костыль.
  birth_city: cityDtoSchema.nullable(),
  birth_lat: z.number().nullable(),
  birth_lon: z.number().nullable(),
  birth_tz: z.string().nullable(),
  vacation_until: isoDateSchema.nullable(),
  forecast_time: timeOfDaySchema,
  forecast_enabled: z.boolean(),
  link_limit: z.number().int().min(0),
  /** Когда завершён онбординг. `null` — регистрация не закончена, показываем её снова. */
  onboarded_at: isoDateTimeSchema.nullable(),
  /**
   * Последняя принятая редакция оферты (`TERMS_VERSION`), либо `null` — не принимал ни одной.
   *
   * Отдаётся в профиле, а не отдельной ручкой: клиент сравнивает это значение с действующей
   * редакцией из контрактов на каждом запуске, и лишний запрос ради одной строки был бы
   * запросом на каждом запуске. `null` бывает у всех, кто зарегистрировался до появления
   * оферты, — им показывается шторка акцепта.
   */
  terms_version: z.string().nullable(),
  last_seen_at: z.string(),
  created_at: z.string(),
})
export type UserDto = z.infer<typeof userDtoSchema>

// GET /users/me
export const meResponseSchema = userDtoSchema
export type MeResponse = z.infer<typeof meResponseSchema>

// PATCH /users/me — имя, пол.
export const updateMeRequestSchema = z.object({
  first_name: z.string().min(1).max(64).optional(),
  gender: genderSchema.optional(),
})
export type UpdateMeRequest = z.infer<typeof updateMeRequestSchema>
export const updateMeResponseSchema = userDtoSchema
export type UpdateMeResponse = z.infer<typeof updateMeResponseSchema>

// PATCH /users/me/birth — дата/время/город рождения.
export const updateBirthRequestSchema = z.object({
  birth_date: isoDateSchema,
  birth_time: timeOfDaySchema.nullable().optional(),
  birth_city_id: idSchema.nullable().optional(),
})
export type UpdateBirthRequest = z.infer<typeof updateBirthRequestSchema>
export const updateBirthResponseSchema = userDtoSchema
export type UpdateBirthResponse = z.infer<typeof updateBirthResponseSchema>

// POST /users/me/onboarding — отметить регистрацию завершённой. Идемпотентно: повторный
// вызов ничего не меняет, поэтому повтор при плохой сети безопасен.
export const completeOnboardingResponseSchema = userDtoSchema
export type CompleteOnboardingResponse = z.infer<typeof completeOnboardingResponseSchema>

// POST /users/me/terms — зафиксировать акцепт действующей редакции оферты.
//
// РЕДАКЦИИ В ТЕЛЕ ЗАПРОСА НЕТ, И ЭТО НЕ УПУЩЕНИЕ. Её подставляет сервер из `TERMS_VERSION`
// (contracts/domain/legalDocs.ts). Позволь мы присылать её клиенту — выкаченная в чужом кэше
// старая мини-аппа записала бы акцепт документа, которого человек не открывал; а именно эта
// запись решает спор о том, на каких условиях заключён договор.
//
// В теле только место нажатия: `onboarding` — строка под кнопкой первого экрана регистрации,
// `resume` — шторка для тех, кто зарегистрировался раньше появления документа. На продукт оно
// не влияет и нужно ровно на один вопрос: почему у человека, зарегистрированного в июле,
// акцепт стоит августом.
//
// Идемпотентна: повторный вызов не создаёт вторую запись и не сдвигает момент акцепта.
export const termsAcceptSourceSchema = z.enum(['onboarding', 'resume'])
export type TermsAcceptSource = z.infer<typeof termsAcceptSourceSchema>

export const acceptTermsRequestSchema = z.object({ source: termsAcceptSourceSchema })
export type AcceptTermsRequest = z.infer<typeof acceptTermsRequestSchema>
export const acceptTermsResponseSchema = userDtoSchema
export type AcceptTermsResponse = z.infer<typeof acceptTermsResponseSchema>

// PATCH /users/me/settings — время прогноза и его включённость.
export const updateSettingsRequestSchema = z.object({
  forecast_time: timeOfDaySchema.optional(),
  forecast_enabled: z.boolean().optional(),
})
export type UpdateSettingsRequest = z.infer<typeof updateSettingsRequestSchema>
export const updateSettingsResponseSchema = userDtoSchema
export type UpdateSettingsResponse = z.infer<typeof updateSettingsResponseSchema>

// PATCH /users/me/vacation — включить (дата окончания) или выключить (null) отпуск.
export const updateVacationRequestSchema = z.object({
  until: isoDateSchema.nullable(),
})
export type UpdateVacationRequest = z.infer<typeof updateVacationRequestSchema>
export const updateVacationResponseSchema = userDtoSchema
export type UpdateVacationResponse = z.infer<typeof updateVacationResponseSchema>

// DELETE /users/me — удаление аккаунта (реальный каскад + тихий разрыв всех связей).
export const deleteMeResponseSchema = okResponseSchema
export type DeleteMeResponse = z.infer<typeof deleteMeResponseSchema>
